import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import {ConfirmationService, MessageService} from 'primeng/api';

import { AccountRequestDto } from '../../../dto/account.request.dto';
import { UserService } from '../../../services/user.service';
import { UserSubject } from '../../../subject/user.subject';
import { OptionResponseDto } from '../../../dto/option.reponse.dto';
import { ResponseDto } from '../../../dto/response.dto';
import { PublicService } from '../../../services/public.service';
import { CountryCodeService } from '../../../services/country.code.service';

@Component({
    selector: 'app-register-personal',
    templateUrl: './register-personal.component.html',
    styleUrls: ['./register-personal.component.scss'],
    providers: [MessageService]
})
export class RegisterPersonalComponent implements OnInit {
    personalForm!: FormGroup;

    loading = false;
    loadData = false;

    identityTypes: OptionResponseDto[] = [];
    targetTypes: OptionResponseDto[] = [];
    countryCodes: { label: string; value: string }[] = [];

    isNotDataValid = false;

    dialogMessage = '';
    dialogCode: string | number | undefined;
    showErrorDialog = false;

    accountRequestDto: Partial<AccountRequestDto> | undefined;

    readonly DEFAULT_COUNTRY_CODE = '+591'; // Bolivia
    readonly yearRange = `1900:${new Date().getFullYear()}`;

    constructor(
        private formBuilder: FormBuilder,
        private router: Router,
        private userService: UserService,
        private userSubject: UserSubject,
        private catalogService: PublicService,
        private countryCodeService: CountryCodeService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.accountRequestDto = this.userSubject.getRegistrationData();
        if (!this.accountRequestDto || Object.keys(this.accountRequestDto).length === 0) {
            this.router.navigate(['/login']);
            return;
        }
        this.initForm();
        this.setupConditionalValidators(); // valida identityNumber por tipo
        this.loadInitialData();
    }

    private initForm(): void {
        this.personalForm = this.formBuilder.group({
            accountAlias: ['', [Validators.required, Validators.maxLength(50)]],
            names: ['', [Validators.required, Validators.maxLength(60)]],
            firstName: ['', [Validators.required, Validators.maxLength(60)]],
            secondName: ['', [Validators.required, Validators.maxLength(60)]],
            ctIdentityType: [null, Validators.required],
            identityNumber: ['', [Validators.required, Validators.maxLength(20), Validators.pattern(/^[0-9]+$/)]],
            birthdate: ['', [Validators.required, this.mayorDeEdadValidator()]],
            cellphoneExtension: [null, Validators.required],
            cellphone: ['', [Validators.required, Validators.pattern(/^[0-9]{7,15}$/)]],
            ctAccountTarget: ['SEARCH_TARGET', Validators.required],
        });
    }

    /**
     * Cambia el patrón de identityNumber según el tipo de documento.
     * - CI_ID → solo dígitos.
     * - PASSPORT / otros → alfanumérico (guiones y espacios permitidos).
     */
    private setupConditionalValidators(): void {
        this.personalForm.get('ctIdentityType')?.valueChanges.subscribe((type) => {
            const control = this.personalForm.get('identityNumber');
            if (!control) return;
            const base = [Validators.required, Validators.maxLength(20)];
            if (type === 'CI_ID') {
                control.setValidators([...base, Validators.pattern(/^[0-9]+$/)]);
            } else {
                control.setValidators([...base, Validators.pattern(/^[A-Za-z0-9\- ]+$/)]);
            }
            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    private loadInitialData(): void {
        forkJoin({
            identityTypes: this.catalogService.getCatalogList('CT_IDENTITY_TYPE')
                .pipe(catchError(err => { this.responseHandler(err); return of([]); })),
            targetTypes: this.catalogService.getCatalogList('CT_ACCOUNT_TARGET')
                .pipe(catchError(err => { this.responseHandler(err); return of([]); })),
            countryCodes: this.countryCodeService.getCountryCodes() // ya tiene fallback
        }).subscribe({
            next: ({ identityTypes, targetTypes, countryCodes }) => {
                this.identityTypes = Array.isArray(identityTypes) ? identityTypes : [];
                this.targetTypes = Array.isArray(targetTypes) ? targetTypes : [];
                this.countryCodes = Array.isArray(countryCodes) ? countryCodes : [];

                const defaultIdentity =
                    this.identityTypes.find(o => o.optionCode === 'CI_ID')?.optionCode
                    ?? this.identityTypes[0]?.optionCode
                    ?? null;

                const defaultCountry =
                    this.countryCodes.find(c => c.value === this.DEFAULT_COUNTRY_CODE)?.value
                    ?? this.countryCodes.find(c => /bolivia/i.test(c.label))?.value
                    ?? this.countryCodes[0]?.value
                    ?? null;

                this.personalForm.patchValue({
                    ctIdentityType: defaultIdentity,
                    cellphoneExtension: defaultCountry
                });

                this.loadData = true;
            },
            error: (error) => {
                console.error('Error en carga inicial', error);
                this.responseHandler(error);
                this.loadData = true;
            }
        });
    }

    onSubmit(): void {
        console.log('[onSubmit] click');
        this.dialogMessage = '';
        this.showErrorDialog = false;

        this.trimAllTextFields(['accountAlias','names','firstName','secondName','identityNumber','cellphone']);

        console.log('[onSubmit] form valid?', this.personalForm.valid, this.personalForm.value);
        if (this.personalForm.invalid) {
            this.isNotDataValid = true;
            this.personalForm.markAllAsTouched();
            this.debugInvalidControls();   // <- clave
            this.scrollToFirstError();
            this.messageService.add({ severity: 'warn', summary: 'Faltan datos', detail: 'Revisa los campos resaltados.' });
            return;
        }

        this.loading = true;
        const finalData: AccountRequestDto = this.buildFinalData();
        console.log('[onSubmit] llamando createUser', finalData);

        this.userService.createUser(finalData).pipe(
            finalize(() => (this.loading = false))
        ).subscribe({
            next: (response) => {
                console.log('[onSubmit]', response);
                this.messageService.add({ severity: 'success', summary: 'Cuenta creada', detail: 'Tu cuenta fue creada con éxito.' });
                this.handleSuccessfulRegistration();
            },
            error: (error) => {
                console.error('[onSubmit] error backend', error);
                this.applyServerErrors(error);   // NO navegamos si hay error
            }
        });
    }

    private debugInvalidControls() {
        const invalid: string[] = [];
        Object.keys(this.personalForm.controls).forEach(key => {
            const c = this.personalForm.get(key);
            if (c && c.invalid) invalid.push(`${key}: ${JSON.stringify(c.errors)}`);
        });
        console.warn('[form invalid controls]', invalid);
    }


    private buildFinalData(): AccountRequestDto {
        const registrationData = this.userSubject.getRegistrationData() || {};
        const birthdate = this.formatBirthdate(this.personalForm.value.birthdate);
        const fullName = this.buildFullName();

        return {
            accountEmail: registrationData.accountEmail || '',
            accountPassword: registrationData.accountPassword || '',
            accountPasswordConfirmation: registrationData.accountPasswordConfirmation || '',
            emailSubscription: registrationData.emailSubscription ?? false,
            acceptance: registrationData.acceptance ?? true,
            accountAlias: this.personalForm.value.accountAlias,
            accountName: fullName,
            ctIdentityType: this.personalForm.value.ctIdentityType,
            identityNumber: this.personalForm.value.identityNumber,
            birthdate,
            cellphoneExtension: this.personalForm.value.cellphoneExtension,
            cellphone: this.personalForm.value.cellphone,
            ctAccountTarget: this.personalForm.value.ctAccountTarget,
        };
    }

    private buildFullName(): string {
        const parts = [
            (this.personalForm.value.names || '').trim(),
            (this.personalForm.value.firstName || '').trim(),
            (this.personalForm.value.secondName || '').trim()
        ].filter(Boolean);
        return parts.join(' ');
    }

    private formatBirthdate(birthdate: Date | string): string {
        if (!birthdate) return '';
        const d = new Date(birthdate);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    private mayorDeEdadValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            const value = control.value;
            if (!value) return null;
            const birthDate = new Date(value);
            const now = new Date();
            const age = this.calculateAge(birthDate, now);
            return age >= 18 ? null : { menorDeEdad: true };
        };
    }

    private calculateAge(birthDate: Date, currentDate: Date): number {
        let age = currentDate.getFullYear() - birthDate.getFullYear();
        const monthDiff = currentDate.getMonth() - birthDate.getMonth();
        const dayDiff = currentDate.getDate() - birthDate.getDate();
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age -= 1;
        return age;
    }

    private handleSuccessfulRegistration(): void {
        this.userSubject.clearData();
        this.router.navigate(['/login']);
    }

    getValidationMessages(control: AbstractControl | null, thingName?: string): string[] {
        if (!control || !control.errors) return [];
        const thing = thingName || 'campo';
        const e = control.errors;
        const messages: string[] = [];

        if (e['required']) messages.push(`El ${thing} es requerido.`);
        if (e['minlength']) messages.push(`El ${thing} debe tener al menos ${e['minlength'].requiredLength} caracteres.`);
        if (e['maxlength']) messages.push(`El ${thing} no debe tener más de ${e['maxlength'].requiredLength} caracteres.`);
        if (e['pattern']) messages.push(`El ${thing} contiene caracteres inválidos.`);
        if (e['email']) messages.push(`El ${thing} debe ser un correo electrónico válido.`);
        if (e['min']) messages.push(`El importe debe ser 1.00 como mínimo.`);
        if (e['menorDeEdad']) messages.push(`Debe ser mayor de 18 años.`);
        if (e['server']) messages.push(e['server']); // mensaje del backend por campo

        return messages;
    }

    responseHandler(responseDto: ResponseDto | any) {
        const errorDetail = responseDto?.errorDetail ?? responseDto?.message ?? 'Ocurrió un error.';
        const statusCode = responseDto?.statusCode ?? responseDto?.status ?? '';
        this.dialogDisplayError(errorDetail, statusCode);
    }

    dialogDisplayError(error: string | null, status: string | number) {
        this.showErrorDialog = true;
        this.dialogMessage = error ?? '';
        this.dialogCode = status;
    }

    private trimAllTextFields(fields: string[]) {
        const patch: Record<string, any> = {};
        fields.forEach(f => {
            const v = (this.personalForm.get(f)?.value ?? '').toString();
            patch[f] = v.trim();
        });
        this.personalForm.patchValue(patch, { emitEvent: false });
    }

    trackByOptionCode = (_: number, item: OptionResponseDto) => item.optionCode;

    /** Pone el foco en el primer control con error (UX) */
    private scrollToFirstError(): void {
        setTimeout(() => {
            const el = document.querySelector('.ng-invalid[formcontrolname]');
            if (el instanceof HTMLElement) el.focus();
        });
    }

    /**
     * Interpreta errores del backend y:
     * - Muestra toast si es general
     * - Marca errores por campo si vienen mapeados
     */
    private applyServerErrors(error: any) {
        // 1) Errores por campo (múltiples formatos comunes)
        const fieldErrors: Array<{ field: string; message: string }> = [];

        if (Array.isArray(error?.error?.errors)) {
            // Ej: Spring BindingResult { errors: [{ field, defaultMessage }]}
            error.error.errors.forEach((e: any) => {
                fieldErrors.push({ field: e.field, message: e.message ?? e.defaultMessage });
            });
        }

        if (error?.error?.fieldErrors && typeof error.error.fieldErrors === 'object') {
            // Ej: { fieldErrors: { identityNumber: ['mensaje1', 'mensaje2'] } }
            Object.entries(error.error.fieldErrors).forEach(([field, msgs]: any) => {
                const message = Array.isArray(msgs) ? msgs[0] : String(msgs);
                fieldErrors.push({ field, message });
            });
        }

        if (error?.error?.validationErrors && typeof error.error.validationErrors === 'object') {
            Object.entries(error.error.validationErrors).forEach(([field, msgs]: any) => {
                const message = Array.isArray(msgs) ? msgs[0] : String(msgs);
                fieldErrors.push({ field, message });
            });
        }

        // 2) Si hay errores por campo, pintarlos en los controles
        if (fieldErrors.length) {
            fieldErrors.forEach(({ field, message }) => {
                const ctrl = this.personalForm.get(field);
                if (ctrl) {
                    ctrl.setErrors({ ...(ctrl.errors || {}), server: message });
                    ctrl.markAsTouched();
                }
            });
            this.isNotDataValid = true;
            this.scrollToFirstError();
        }

        // 3) Mensaje general (sin navegar)
        const generalMessage =
            error?.errorDetail ||
            error?.error?.errorDetail ||
            error?.error?.message ||
            error?.message ||
            'No pudimos completar el registro. Verifica los datos.';

        this.messageService.add({
            severity: 'error',
            summary: 'No se pudo crear la cuenta',
            detail: generalMessage
        });

        // Además, mantener el bloque de diálogo si lo estás usando en el template:
        this.responseHandler(error);
    }
}
